import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import PricingSection from './components/PricingSection';
import WhatMakesCynizeeDifferent from './components/WhatMakesCynizeeDifferent';
import CoreLaunchFeatures from './components/CoreLaunchFeatures';
import FeatureHighlights from './components/FeatureHighlights';
import WhyFamiliesLoveIt from './components/WhyFamiliesLoveIt';
import ComingSoonSection from './components/ComingSoonSection';
import FooterSection from './components/FooterSection';

const Homepage = () => {
  useEffect(() => {
    const handleSmoothScroll = (e) => {
      const href = e?.currentTarget?.getAttribute('href');
      if (href && href?.startsWith('#')) {
        e?.preventDefault();
        const target = document.querySelector(href);
        if (target) {
          target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    };

    const anchorLinks = document.querySelectorAll('a[href^="#"]');
    anchorLinks?.forEach(link => link?.addEventListener('click', handleSmoothScroll));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('revealed');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const scrollElements = document.querySelectorAll('.scroll-reveal');
    scrollElements?.forEach(el => observer?.observe(el));

    return () => {
      anchorLinks?.forEach(link => link?.removeEventListener('click', handleSmoothScroll));
      observer?.disconnect();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Cynizee — Less Nagging. More Accountability. Better Habits.</title>
        <meta
          name="description"
          content="Cynizee helps families manage chores, build responsibility, and create real follow-through with reminders, approvals, proof of completion, and smart accountability tools."
        />
        <meta
          name="keywords"
          content="family chore app, kids accountability, chore tracking, parental controls, app locking, chore assignment, family organization"
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Cynizee — Less Nagging. More Accountability. Better Habits." />
        <meta
          property="og:description"
          content="Smart family chore and accountability app. Assign chores, track progress, require proof, and lock apps until chores are done."
        />
        <meta property="og:url" content="https://cynizee.com" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Cynizee — Less Nagging. More Accountability. Better Habits." />
        <meta
          name="twitter:description"
          content="Smart family chore and accountability app. Join the waitlist for early access."
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Cynizee Team" />
        <link rel="canonical" href="https://cynizee.com" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <main>
          <section id="hero" className="scroll-reveal">
            <HeroSection />
          </section>

          <section id="about" className="scroll-reveal">
            <AboutSection />
          </section>

          <section id="pricing" className="scroll-reveal">
            <PricingSection />
          </section>

          <section id="what-makes-different" className="scroll-reveal">
            <WhatMakesCynizeeDifferent />
          </section>

          <section id="core-features" className="scroll-reveal">
            <CoreLaunchFeatures />
          </section>

          <section id="features" className="scroll-reveal">
            <FeatureHighlights />
          </section>

          <section id="why-families" className="scroll-reveal">
            <WhyFamiliesLoveIt />
          </section>

          <section id="coming-soon" className="scroll-reveal">
            <ComingSoonSection />
          </section>
        </main>

        <FooterSection />
      </div>
    </>
  );
};

export default Homepage;